/**
 * Path Parser Utility
 * Parses storage paths (unc:// or blob://) into tree structure
 */

export interface TreeNode {
  name: string;
  path: string;
  fullPath: string;
  type: 'folder' | 'file';
  children: TreeNode[];
  artifacts: any[]; // Artifact objects at this node
  fileCount: number;
  totalSize: number;
}

export function parseStoragePath(storagePath: string): {
  protocol: string;
  path: string;
  parts: string[];
} {
  // Handle different path formats
  let cleanPath = storagePath;
  
  // Remove protocol prefix (unc://, blob://, etc.)
  if (storagePath.includes('://')) {
    const [protocol, path] = storagePath.split('://', 2);
    return {
      protocol: protocol,
      path: path,
      parts: path.split('/').filter(p => p.length > 0),
    };
  }
  
  // Handle Windows UNC paths (\\server\share\path)
  if (storagePath.startsWith('\\\\')) {
    const path = storagePath.substring(2); // Remove \\
    return {
      protocol: 'unc',
      path: path,
      parts: path.split(/[/\\]/).filter(p => p.length > 0),
    };
  }
  
  // Handle regular paths
  return {
    protocol: 'local',
    path: storagePath,
    parts: storagePath.split(/[/\\]/).filter(p => p.length > 0),
  };
}

export function buildFileTree(artifacts: any[]): TreeNode {
  const root: TreeNode = {
    name: 'Root',
    path: '',
    fullPath: '',
    type: 'folder',
    children: [],
    artifacts: [],
    fileCount: 0,
    totalSize: 0,
  };

  const nodeMap = new Map<string, TreeNode>();
  nodeMap.set('', root);

  artifacts.forEach(artifact => {
    const storagePath = artifact.storage_path || '';
    const parsed = parseStoragePath(storagePath);
    
    // Build path parts
    const parts = parsed.parts;
    if (parts.length === 0) {
      // Root level file
      root.artifacts.push(artifact);
      root.fileCount++;
      root.totalSize += artifact.file_size_bytes || 0;
      return;
    }

    // Build tree path
    let currentPath = '';
    parts.forEach((part, index) => {
      const isFile = index === parts.length - 1;
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!nodeMap.has(currentPath)) {
        const node: TreeNode = {
          name: part,
          path: part,
          fullPath: currentPath,
          type: isFile ? 'file' : 'folder',
          children: [],
          artifacts: [],
          fileCount: 0,
          totalSize: 0,
        };

        nodeMap.set(currentPath, node);

        // Add to parent
        const parent = nodeMap.get(parentPath);
        if (parent) {
          parent.children.push(node);
        }
      }

      const node = nodeMap.get(currentPath)!;
      
      if (isFile) {
        // This is the file node
        node.artifacts.push(artifact);
        node.fileCount++;
        node.totalSize += artifact.file_size_bytes || 0;
      }

      // Update parent counts
      let parent = nodeMap.get(parentPath);
      while (parent) {
        parent.fileCount++;
        parent.totalSize += artifact.file_size_bytes || 0;
        const parentParts = parent.fullPath.split('/').filter(p => p);
        if (parentParts.length > 0) {
          parentParts.pop();
          parent = nodeMap.get(parentParts.join('/'));
        } else {
          parent = undefined;
        }
      }
    });
  });

  // Sort children
  function sortNode(node: TreeNode) {
    node.children.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    node.children.forEach(sortNode);
  }
  sortNode(root);

  return root;
}



